<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Table from 'dashboard/components-next/table/Table.vue';
import { useTaskNavigation } from 'dashboard/composables/useTaskNavigation';
import {
  getTaskDueBucket,
  formatTaskDueLabel,
  isTaskOverdue,
  isTaskDueToday,
} from 'dashboard/composables/useTaskDueDate';

const props = defineProps({
  tasks: { type: Array, required: true },
});

const emit = defineEmits(['statusChange', 'delete', 'edit']);

const { t } = useI18n();
const { openTask } = useTaskNavigation();

// Percentage widths keep the table inside its container at any viewport size,
// so the list never needs a horizontal scrollbar.
const columns = computed(() => [
  { key: 'task', label: t('TASKS_DASHBOARD.TABLE.TASK'), width: 'w-[38%]' },
  { key: 'dueAt', label: t('TASKS_DASHBOARD.TABLE.DUE_AT'), width: 'w-[17%]' },
  {
    key: 'assignee',
    label: t('TASKS_DASHBOARD.TABLE.ASSIGNEE'),
    width: 'w-[18%]',
  },
  {
    key: 'contact',
    label: t('TASKS_DASHBOARD.TABLE.CONTACT'),
    width: 'w-[22%]',
  },
  { key: 'actions', label: '', width: 'w-[5%]' },
]);

// Order matters: the list reads most-urgent-first, same as the kanban's
// left-to-right workflow reads least-to-most-done.
const BUCKET_ORDER = ['overdue', 'today', 'this_week', 'later', 'no_date'];
const BUCKET_DOT = {
  overdue: 'bg-n-ruby-9',
  today: 'bg-n-blue-9',
  this_week: 'bg-n-teal-9',
  later: 'bg-n-slate-8',
  no_date: 'bg-n-slate-6',
};
const BUCKET_LABEL_KEY = {
  overdue: 'TASKS_DASHBOARD.DUE.OVERDUE',
  today: 'TASKS_DASHBOARD.DUE.TODAY',
  this_week: 'TASKS_DASHBOARD.DUE.THIS_WEEK',
  later: 'TASKS_DASHBOARD.DUE.LATER',
  no_date: 'TASKS_DASHBOARD.DUE.NO_DATE',
};

// Una tarea sin asignar sigue siendo de quien la creó: el backend ya la
// trata así para los recordatorios (Task#reminder_recipient), así que se
// muestra ese dueño implícito en vez de un "Sin asignar" pelado.
const toRow = task => ({
  ...task,
  task,
  isCompleted: task.status === 'done',
  dueLabel: formatTaskDueLabel(task, t),
  isOverdue: isTaskOverdue(task),
  isDueToday: isTaskDueToday(task),
  owner: task.assignee || task.createdBy,
  isImplicitOwner: !task.assignee && !!task.createdBy,
  contactName: task.contact?.name || t('TASKS_DASHBOARD.UNNAMED_CONTACT'),
  companyName: task.contact?.company?.name || '',
});

const groups = computed(() => {
  const byBucket = Object.fromEntries(BUCKET_ORDER.map(key => [key, []]));
  props.tasks.forEach(task => {
    byBucket[getTaskDueBucket(task)].push(toRow(task));
  });
  return BUCKET_ORDER.map(bucket => ({
    key: bucket,
    label: t(BUCKET_LABEL_KEY[bucket]),
    dot: BUCKET_DOT[bucket],
    rows: byBucket[bucket],
  })).filter(group => group.rows.length);
});

const toggleCompleted = row =>
  emit('statusChange', row.id, row.isCompleted ? 'todo' : 'done');
</script>

<template>
  <Table
    :columns="columns"
    :groups="groups"
    density="regular"
    clickable
    @row-click="row => emit('edit', row.task)"
  >
    <template #cell-task="{ row }">
      <div class="flex items-start gap-3 min-w-0">
        <button
          type="button"
          class="mt-px flex-shrink-0 transition-transform hover:scale-110"
          @click.stop="toggleCompleted(row)"
        >
          <Icon
            :icon="
              row.isCompleted ? 'i-lucide-circle-check-big' : 'i-lucide-circle'
            "
            :size="
              row.isCompleted
                ? 'size-[1.125rem] text-n-teal-11'
                : 'size-[1.125rem] text-n-slate-8 hover:text-n-teal-11'
            "
          />
        </button>
        <span
          class="text-sm leading-snug whitespace-pre-line line-clamp-2 min-w-0"
          :class="
            row.isCompleted
              ? 'line-through text-n-slate-10'
              : 'text-n-slate-12 font-medium'
          "
        >
          {{ row.description }}
        </span>
      </div>
    </template>

    <template #cell-dueAt="{ row }">
      <span
        v-if="row.dueLabel"
        class="inline-flex items-center gap-1.5 text-xs whitespace-nowrap rounded-full px-2 py-1 max-w-full"
        :class="{
          'bg-n-ruby-9/10 text-n-ruby-11 font-medium': row.isOverdue,
          'bg-n-blue-9/10 text-n-blue-11 font-medium': row.isDueToday,
          'text-n-slate-11': !row.isOverdue && !row.isDueToday,
        }"
      >
        <Icon
          :icon="row.isOverdue ? 'i-lucide-alarm-clock' : 'i-lucide-calendar'"
          size="size-3.5 flex-shrink-0"
        />
        <span class="truncate">{{ row.dueLabel }}</span>
      </span>
      <span v-else class="text-xs text-n-slate-9">
        {{ t('TASKS_DASHBOARD.TABLE.NO_DUE_AT') }}
      </span>
    </template>

    <template #cell-assignee="{ row }">
      <div v-if="row.owner" class="flex items-center gap-2 min-w-0">
        <Avatar
          :name="row.owner.name"
          :src="row.owner.thumbnail"
          :size="24"
          rounded-full
        />
        <span class="text-sm text-n-slate-12 truncate">
          {{ row.owner.name }}
          <span v-if="row.isImplicitOwner" class="text-n-slate-9 font-normal">
            · {{ t('TASKS_DASHBOARD.CREATED_BY_SUFFIX') }}
          </span>
        </span>
      </div>
      <div v-else class="flex items-center gap-2 text-n-slate-9 min-w-0">
        <span
          class="size-6 rounded-full border border-dashed border-n-slate-7 flex items-center justify-center flex-shrink-0"
        >
          <Icon icon="i-lucide-user" size="size-3" />
        </span>
        <span class="text-sm truncate">
          {{ t('TASKS_DASHBOARD.NO_ASSIGNEE') }}
        </span>
      </div>
    </template>

    <template #cell-contact="{ row }">
      <div class="flex items-center gap-2 min-w-0">
        <Avatar
          :name="row.contactName"
          :src="row.contact?.thumbnail"
          :size="24"
          rounded-full
        />
        <div class="flex flex-col min-w-0">
          <span class="text-sm text-n-slate-12 truncate">
            {{ row.contactName }}
          </span>
          <span v-if="row.companyName" class="text-xs text-n-slate-10 truncate">
            {{ row.companyName }}
          </span>
        </div>
      </div>
    </template>

    <template #cell-actions="{ row }">
      <div
        class="flex items-center justify-end gap-0.5 opacity-0 group-hover/row:opacity-100 transition-opacity"
        @click.stop
      >
        <Button
          variant="ghost"
          color="slate"
          size="xs"
          icon="i-lucide-external-link"
          @click="openTask(row.task)"
        />
        <Button
          variant="ghost"
          color="ruby"
          size="xs"
          icon="i-lucide-trash"
          @click="emit('delete', row.id)"
        />
      </div>
    </template>
  </Table>
</template>
